import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import FullCalendar from "@fullcalendar/react";
import { createPlugin } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import CalendarToolbar from "./CalendarToolbar";
import CalendarEventContent, { describeCalendarEvent } from "./CalendarEventContent";
import { snapAppointmentDrag } from "./snapAppointmentDrag";
import "../Scheduling.css";
import "./CalendarSurface.css";

const SchedulingCalendar = ({
  events = [],
  timeZone = "UTC",
  initialView = "timeGridWeek",
  initialDate,
  headerToolbar,
  selectable = false,
  readOnly = false,
  selectedEventId,
  onDatesSet,
  onEventClick,
  onSelect,
  onDateClick,
  extraViews,
  slotMinutes = 20,
  controls,
  timeZoneControl,
  canDragAppointment,
  resolveAppointmentMove,
  onAppointmentMove,
}) => {
  const calendarRef = useRef(null);
  const scrollRef = useRef(null);
  const draggedEvent = useRef(null);
  const moveResolver = useRef(resolveAppointmentMove);
  useEffect(() => { moveResolver.current = resolveAppointmentMove; }, [resolveAppointmentMove]);
  const bookingSnapPlugin = useMemo(() => createPlugin({ name: "scheduling-booking-snap",
    eventDragMutationMassagers: [(mutation, initialHit) => snapAppointmentDrag(mutation, initialHit, draggedEvent.current, moveResolver.current)],
  }), []);
  const [visibleRange, setVisibleRange] = useState(null);
  const [viewType, setViewType] = useState(initialView);
  const [viewTitle, setViewTitle] = useState("");
  const [draggingAppointment, setDraggingAppointment] = useState(false);
  const [viewDate, setViewDate] = useState(() => moment.tz(initialDate || undefined, timeZone).format("YYYY-MM-DD"));
  const renderedEvents = useMemo(() => events.map((event) => {
    const rendered = viewType.startsWith("timeGrid") && event.extendedProps?.kind === "availability"
      ? { ...event, display: "background" }
      : event;
    return onAppointmentMove ? { ...rendered, startEditable: !readOnly && viewType.startsWith("timeGrid") && !!canDragAppointment?.(event), durationEditable: false } : rendered;
  }), [events, viewType, onAppointmentMove, canDragAppointment, readOnly]);
  const plugins = [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, momentTimezonePlugin, bookingSnapPlugin];
  const views = extraViews || "timeGridWeek,timeGridDay,dayGridMonth";
  const scrollMinute = useMemo(() => {
    if (!visibleRange) return 0;
    const start = moment.tz(visibleRange.start, timeZone);
    const end = moment.tz(visibleRange.end, timeZone);
    const minutes = events.flatMap((event) => {
      const eventStart = moment.tz(event.start, timeZone);
      const eventEnd = moment.tz(event.end, timeZone);
      if (!eventStart.isValid() || !eventEnd.isValid() || !eventEnd.isAfter(start) || !eventStart.isBefore(end)) return [];
      // Overnight events occupy the beginning of the following visible day.
      const lastVisibleInstant = moment.min(eventEnd, end).clone().subtract(1, "millisecond");
      if (eventStart.isBefore(start) || !eventStart.isSame(lastVisibleInstant, "day")) return [0];
      return [eventStart.hour() * 60 + eventStart.minute()];
    });
    return minutes.length ? Math.min(...minutes) : 0;
  }, [events, timeZone, visibleRange]);
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !viewType.startsWith("timeGrid")) return;
    const frame = requestAnimationFrame(() => {
      const rows = [...container.querySelectorAll(".fc-timegrid-slot-lane[data-time]")];
      const row = rows.filter((item) => {
        const [hour, minute] = item.dataset.time.split(":").map(Number);
        return hour * 60 + minute <= scrollMinute;
      }).pop();
      if (row && rows[0]) container.scrollTop = row.getBoundingClientRect().top - rows[0].getBoundingClientRect().top;
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollMinute, viewDate, viewType, timeZone, slotMinutes]);

  return (
    <div className={`scheduling-calendar-wrap schedule-calendar-surface ${viewType}`} style={{ "--schedule-slot-height": `${slotMinutes * 1.5}px` }}>
      {!headerToolbar && <CalendarToolbar title={viewTitle || moment.tz(viewDate, timeZone).format("MMMM YYYY")} date={viewDate}
        timeZone={timeZone} timeZoneControl={timeZoneControl} viewType={viewType} views={views}
        onNavigate={(action) => calendarRef.current?.getApi?.()[action]()}
        onViewChange={(view) => calendarRef.current?.getApi?.().changeView(view)}
        onDateChange={(date) => calendarRef.current?.getApi?.().gotoDate(date)} />}
      {controls && <div className="schedule-calendar-controls">{controls}</div>}
      <p className="schedule-calendar-scroll-hint">Scroll across to see the full week, or choose Day.</p>
      <div className="schedule-calendar-grid" ref={scrollRef}>
      <FullCalendar
        ref={calendarRef}
        plugins={plugins}
        initialView={initialView}
        initialDate={initialDate}
        headerToolbar={headerToolbar || false}
        timeZone={timeZone}
        firstDay={1}
        allDaySlot={false}
        slotDuration={{ minutes: slotMinutes }}
        snapDuration={{ minutes: draggingAppointment ? 1 : slotMinutes }}
        slotLabelInterval={{ hours: 1 }}
        slotEventOverlap={false}
        eventMinHeight={28}
        dayHeaderContent={({ date, isToday, view, text }) => {
          const day = moment(date).tz(timeZone);
          return <div className={`schedule-calendar-day-heading${isToday ? " is-today" : ""}`}>
            <span>{view.type.startsWith("timeGrid") ? day.format("ddd") : text}</span>{view.type.startsWith("timeGrid") && <strong>{day.format("D")}</strong>}
          </div>;
        }}
        buttonText={{ today: "Today", month: "Month", week: "Week", day: "Day", list: "List" }}
        events={renderedEvents}
        eventContent={({ event, view, isMirror }) => {
          if (isMirror && onAppointmentMove && event.extendedProps.kind === "appointment") {
            const from = moment(event.start).tz(timeZone);
            const to = moment(event.end).tz(timeZone);
            const valid = !!resolveAppointmentMove?.(event, event.start);
            return <span className="schedule-calendar-move-time">{`${from.format("h:mm A")}–${to.format("h:mm A z")}${valid ? "" : " · No valid start here"}`}</span>;
          }
          if (event.display !== "background" || event.extendedProps.kind !== "availability") {
            return view?.type?.startsWith("list") ? true : <CalendarEventContent event={event} timeZone={timeZone} viewType={view?.type || viewType} />;
          }
          const range = describeCalendarEvent(event, timeZone).range;
          const description = `Working hours · ${range} · ${event.title}`;
          const content = <><strong className="schedule-working-hours-label">Working hours</strong>
            <span className="schedule-working-hours-range">{range}</span>
            <span className="schedule-working-hours-types">{event.title}</span></>;
          return onEventClick && !readOnly ? (
            <button type="button" className="schedule-working-hours-content"
              aria-label={description} title={description}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEventClick(event, event.extendedProps);
              }}>{content}</button>
          ) : <div className="schedule-working-hours-content" role="group" aria-label={description} title={description}>{content}</div>;
        }}
        eventDidMount={({ event, el }) => {
          if (event.extendedProps.kind === "availability") return;
          const description = describeCalendarEvent(event, timeZone).description;
          el.setAttribute("aria-label", description);
          el.setAttribute("title", description);
        }}
        nowIndicator
        selectable={selectable && !readOnly}
        selectMirror={selectable && !readOnly}
        selectAllow={(selection) => moment(selection.end).diff(moment(selection.start), "hours", true) <= 24}
        editable={false}
        eventStartEditable={false}
        eventDurationEditable={false}
        eventDragStart={({ event }) => { draggedEvent.current = event; setDraggingAppointment(true); }}
        eventDragStop={() => { draggedEvent.current = null; setDraggingAppointment(false); }}
        eventAllow={(drop, event) => !readOnly && !!onAppointmentMove && !!canDragAppointment?.(event) && !!resolveAppointmentMove?.(event, drop.start)}
        eventDrop={(info) => {
          const move = !readOnly && canDragAppointment?.(info.oldEvent) ? resolveAppointmentMove?.(info.oldEvent, info.event.start) : null;
          // The calendar must keep its persisted dates until the review is confirmed.
          info.revert();
          if (move && +new Date(move.startsAt) !== +new Date(move.appointment.startsAt)) onAppointmentMove?.(move);
        }}
        slotMinTime="00:00:00"
        slotMaxTime="24:00:00"
        height="auto"
        eventClassNames={(arg) => [
          `schedule-event-${arg.event.extendedProps.kind || "availability"}`,
          ...(arg.event.extendedProps.kind === "appointment" && arg.event.extendedProps.source?.status
            ? [`schedule-status-${arg.event.extendedProps.source.status}`] : []),
          ...(selectedEventId && arg.event.id === selectedEventId ? ["scheduling-event-selected"] : []),
        ]}
        datesSet={(arg) => {
          const nextRange = { start: arg.start.toISOString(), end: arg.end.toISOString() };
          setVisibleRange((current) => current?.start === nextRange.start && current?.end === nextRange.end ? current : nextRange);
          setViewType(arg.view.type);
          if (arg.view.title) setViewTitle(arg.view.title);
          if (arg.view.currentStart) setViewDate(moment(arg.view.currentStart).tz(timeZone).format("YYYY-MM-DD"));
          onDatesSet?.({
            start: arg.start,
            end: arg.end,
            view: arg.view,
            calendar: calendarRef.current?.getApi?.(),
          });
        }}
        eventClick={(info) => {
          info.jsEvent.preventDefault();
          onEventClick?.(info.event, info.event.extendedProps);
        }}
        select={(info) => {
          onSelect?.(info);
          calendarRef.current?.getApi?.().unselect();
        }}
        dateClick={(info) => {
          if (info.view.type === "dayGridMonth") {
            info.view.calendar.changeView("timeGridDay", info.date);
          }
          onDateClick?.(info);
        }}
      />
      </div>
    </div>
  );
};

export default SchedulingCalendar;
